/**
 * Stimulus Controller: remote-into-modal
 * --------------------------------------
 * This controller dynamically loads content into a Bootstrap modal via AJAX.
 * It fetches content when the associated element (e.g., link or button) is clicked,
 * and displays it in a modal with configurable options.
 *
 * Usage Example (HTML/ERB):
 * -------------------------
 * ```
 * <a href="/products/1"
 *   data-controller="remote-into-modal"
 *   data-action="click->remote-into-modal#open"
 *   data-remote-into-modal-modal-size-value="lg"
 *   data-remote-into-modal-modal-id-value="product-modal"
 *   data-remote-into-modal-title-value="Product Details">
 *   View Product
 * </a>
 * ```
 * Usage Example (HAML):
 * -------------------------
 * ```
 *    link_to product.name,
 *      [basket, product],
 *      data: { controller: 'remote-into-modal',
 *              action: "click->remote-into-modal#open",
 *              'remote-into-modal-modal-size-value': 'lg',
 *              'remote-into-modal-modal-id-value': '#content-goes-into-dom-element-with-this-id',
 *              'remote-into-modal-title-value': "Modal Title" }
 * ```
 *
 * Required Data Attributes:
 * -------------------------
 * - `data-controller="remote-into-modal"` → Attaches the controller.
 * - `data-action="click->remote-into-modal#open"` → Triggers modal opening on click.
 * - `data-remote-into-modal-modal-size-value="lg"` → (Optional) Bootstrap modal size (e.g., sm, lg, xl).
 * - `data-remote-into-modal-modal-id-value="product-modal"` → Unique ID for the modal to avoid duplication.
 * - `data-remote-into-modal-title-value="Product Details"` → Title to display inside the modal.
 *
 * Expected Behavior:
 * ------------------
 * 1. Prevents the default action of the element.
 * 2. Sends an AJAX (fetch) request to the `href` of the clicked element.
 * 3. Injects the response content into a Bootstrap modal.
 * 4. Shows the modal to the user.
 * 5. Handles errors gracefully by displaying a fallback error message.
 * 6. Removes any existing modal backdrop before rendering a new modal.
 *
 * Dependencies:
 * -------------
 * - Bootstrap 5 (for modal functionality)
 * - StimulusJS (for data-action binding)
 *
 * Methods:
 * --------
 * - `open(event)`: Handles click event, fetches content, and opens modal.
 * - `removeExistingBackdrops()`: Removes any existing modal backdrop elements to prevent duplication.
 *
 * Notes:
 * ------
 * - Ensure Bootstrap's JavaScript is loaded for modal functionality.
 * - The modal ID should be unique to avoid conflicts with other instances.
 */

import { Controller } from "@hotwired/stimulus";
import { initializeSelect2 } from "./utils/initialize_select2";

export default class extends Controller {
  static values = {
    modalSize: String,
    modalId: String,
    title: String
  };

  async open(event) {
    event.stopPropagation()
    event.preventDefault(); // Prevent default link behavior

    if (event.target.closest('a') && event.target.closest('a').hasAttribute('disabled')) {
      return;
    }

    const href = this.element.href;
    const targetId = this.modalIdValue.replace("#", "");
    const modalSize = this.modalSizeValue || "";
    const title = this.titleValue || "Modal Title";

    this.removeExistingBackdrops();

    // Fetch the content
    let responseHTML = "";
    try {
      const response = await fetch(href, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);
      responseHTML = await response.text();
    } catch (error) {
      console.error("Error fetching content:", error);
      responseHTML = '<p class="text-danger">Error loading content. Please try again.</p>';
    }

    // Check if the modal exists or create it
    let modal = document.getElementById(targetId);
    if (!modal) {
      modal = document.createElement("div");
      modal.id = targetId;
      modal.className = "modal fade";
      modal.setAttribute("tabindex", "-1");
      modal.setAttribute("aria-hidden", "true");
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = responseHTML;

      const hasInnerBody = tempDiv.querySelector(":scope > .modal-body") || tempDiv.querySelector(":scope > form .card-body");
      const hasFooter = tempDiv.querySelector(":scope > .modal-footer") || tempDiv.querySelector(":scope > form .card-footer");

      const outerBodyClass = hasInnerBody || hasFooter ? "p-0 overflow-auto" : "overflow-auto";

      modal.innerHTML = `
        <div class="modal-dialog modal-${modalSize}">
          <div class="modal-content">
            <div class="modal-header bg-primary">
              <h1 class="modal-title text-truncate text-nowrap">${title}</h1>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-hidden="true" aria-label="Close" />
            </div>
            <div class="modal-body ${outerBodyClass}">${responseHTML}</div>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
    } else {
      // Update existing modal content
      modal.querySelector(".modal-title").textContent = title;
      modal.querySelector(".modal-content").appendChild = responseHTML;
    }

    // Initialize and show the modal
    const modalInstance = new bootstrap.Modal(modal);
    initializeSelect2();
    modalInstance.show();
  }

  removeExistingBackdrops() {
    // Remove any existing modal-backdrop elements
    const backdrops = document.querySelectorAll(".modal-backdrop");
    backdrops.forEach((backdrop) => backdrop.remove());
  }
}
